import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { getFeaturedShorts, getShortById, getShorts, getShortsByCategory, likeShort, unlikeShort } from '../api/getShortsApi';


// Hook to fetch shorts with pagination
export const useShorts = (page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data: shortsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['shorts', page, limit],
    () => getShorts(page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch shorts';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
    }
  );

  return {
    shorts: shortsData?.data || [],
    totalShorts: shortsData?.total || 0,
    totalPages: shortsData?.totalPages || 0,
    currentPage: shortsData?.currentPage || page,
    isLoading,
    error,
    refetch
  };
};

// Hook for infinite loading of shorts
export const useInfiniteShorts = (limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useInfiniteQuery(
    ['infiniteShorts', limit],
    ({ pageParam = 1 }) => getShorts(pageParam, limit),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.currentPage < lastPage.totalPages) {
          return lastPage.currentPage + 1;
        }
        return undefined;
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch shorts';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
    }
  );

  // Flatten the pages data
  const shorts = data?.pages.flatMap(page => page.data) || [];
  const totalShorts = data?.pages[0]?.total || 0;

  return {
    shorts,
    totalShorts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  };
};

// Hook to fetch a single short by ID or slug
export const useShort = (shortId) => {
  const { setError } = useAuthStore();
  
  const {
    data: short,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['short', shortId],
    () => getShortById(shortId),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch short';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour,
      enabled: !!shortId
    }
  );

  return { short, isLoading, error, refetch };
};

// Hook to fetch featured shorts
export const useFeaturedShorts = (limit = 5) => {
  const { setError } = useAuthStore();
  
  const {
    data: featuredData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['featuredShorts', limit],
    () => getFeaturedShorts(limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch featured shorts';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
    }
  );

  return {
    featuredShorts: featuredData?.data || [],
    isLoading,
    error,
    refetch
  };
};

// Hook to fetch shorts by category
export const useShortsByCategory = (categoryId, page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data: shortsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['shortsByCategory', categoryId, page, limit],
    () => getShortsByCategory(categoryId, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch shorts by category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour,
      enabled: !!categoryId
    }
  );

  return {
    shorts: shortsData?.data || [],
    totalShorts: shortsData?.total || 0,
    totalPages: shortsData?.totalPages || 0,
    currentPage: shortsData?.currentPage || page,
    isLoading,
    error,
    refetch
  };
};

// Hook to like a short
export const useLikeShort = () => {
  const { setError } = useAuthStore();
  const queryClient = useQueryClient();
  
  const likeMutation = useMutation(
    (shortId) => likeShort(shortId),
    {
      onSuccess: (data, shortId) => {
        // Update the cache for individual short
        queryClient.setQueryData(['short', shortId], (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              likes_count: data.likes
            };
          }
          return oldData;
        });
        
        // Invalidate shorts queries to refresh lists
        queryClient.invalidateQueries('shorts');
        queryClient.invalidateQueries('featuredShorts');
        queryClient.invalidateQueries('shortsByCategory');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to like short';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  );
  
  return {
    likeShort: likeMutation.mutate,
    isLiking: likeMutation.isLoading
  };
};

export const useUnlikeShort = () => {
  const { setError } = useAuthStore();
  const queryClient = useQueryClient();

  const unlikeMutation = useMutation(
    (shortId) => unlikeShort(shortId),
    {
      onSuccess: (data, shortId) => {
        // Update the cache for individual short
        queryClient.setQueryData(['short', shortId], (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              likes_count: data.likes
            };
          }
          return oldData;
        });

        // Invalidate shorts queries to refresh lists
        queryClient.invalidateQueries('shorts');
        queryClient.invalidateQueries('featuredShorts');
        queryClient.invalidateQueries('shortsByCategory');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to unlike short';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  );

  return {
    unlikeShort: unlikeMutation.mutate,
    isUnliking: unlikeMutation.isLoading
  };
};